import type { BranchParkingConfig, DiscountDetail, ParkingFeeApiResponse, ParkingFeeDetail } from '@/services/dto/parking';
import { formatAmount, formatDate, formatDateTimeToKorean, formatMinutesToKorean, formatTime } from '@/utils/parking';
import KeyValueRow from '../key-value-row';
import { useTranslation } from 'react-i18next';
import ParkingActionButtons from './ParkingActionButtons';
import { useTickSoundInteraction } from '@/hooks';
import { PARKING_RESULT_CODE } from '@/constants/parking';

interface DetailContentProps {
    isUseLocation?: boolean;
    parkingFeeApiData: ParkingFeeDetail | null;
    parkingFeeData: BranchParkingConfig | null;
    onBack: () => void;
    handleDirectionGuide: () => void;
    rawData: ParkingFeeApiResponse | null;
}

export default function DetailContent({
    isUseLocation,
    parkingFeeApiData,
    parkingFeeData,
    onBack,
    handleDirectionGuide,
    rawData,
}: DetailContentProps) {
    const { feeStdList = [] } = parkingFeeData || {};
    const { t } = useTranslation();
    const { playTick } = useTickSoundInteraction();

    const parkingData = {
        inDate: parkingFeeApiData?.inDate || '',
        inTime: parkingFeeApiData?.inTime || '',
        parkingTime: parkingFeeApiData?.parkingTime ?? 0,
        discountTime: parkingFeeApiData?.discountTime ?? 0,
        discountDetail: parkingFeeApiData?.discountDetail || [],
        amountTotal: parkingFeeApiData?.amountTotal?.toString() || '0',
    };

    const formattedData = {
        date: formatDate(parkingData.inDate),
        time: formatTime(parkingData.inTime),
        parkingTime: formatMinutesToKorean(parkingData.parkingTime),
        discountTime: formatMinutesToKorean(parkingData.discountTime),
        finalParkingTime: formatMinutesToKorean(Math.max(0, parkingData.parkingTime - parkingData.discountTime)),
        amountTotal: formatAmount(parkingData.amountTotal),
    };
    const formattedDateTime = formatDateTimeToKorean(formattedData.date, formattedData.time);

    // Function to search again (clear and return to main page)
    const handleSearchAgain = () => {
        onBack();
    };

    // Check if rawData exists and has resultCode === 'E' (Scheduled Parking)
    const isScheduledParking = rawData?.resultCode === PARKING_RESULT_CODE.SCHEDULED_PARKING;

    // Only show detail content if rawData exists and is not scheduled parking
    const shouldShowDetail = rawData && !isScheduledParking;

    return (
        <div className="flex flex-col items-center gap-[calc(40*var(--vh-unit))]" data-region-nav data-region-order={2}>
            {/* License plate */}
            <div className={`fullscreen:widescreen:h-[calc(757*var(--vh-unit))] ${isScheduledParking ? 'h-[calc(508*var(--vh-unit))]' : ''} widescreen:h-[calc(589*var(--vh-unit))] widescreen:w-[calc(805*var(--vw-unit))] flex w-[calc(872*var(--vw-unit))] flex-col items-center justify-between gap-[calc(20*var(--vh-unit))] rounded-[calc(20*var(--vh-unit))] border-[calc(1*var(--vh-unit))] border-[var(--color-border-transparent)] bg-[var(--color-bg-gray-f4-to-dark)] p-[calc(64*var(--vh-unit))]`}>
                {isScheduledParking ? (
                    <div className="flex h-full w-full flex-col items-center justify-center p-[calc(32*var(--vh-unit))] text-center">
                        <div
                            className="text-center text-[calc(20*var(--vh-unit))] leading-[calc(32*var(--vh-unit))] font-medium text-[var(--color-sub01)]"
                        >
                            {rawData?.message}
                        </div>
                    </div>
                ) : shouldShowDetail ? (
                    <div className="widescreen:w-[calc(675*var(--vw-unit))] scrollbar-custom-gray-99938f scrollbar-w-4 flex max-h-[calc(700*var(--vh-unit))] flex-col items-start gap-[calc(12*var(--vh-unit))] overflow-y-auto pb-[calc(20*var(--vh-unit))]">
                        <div
                            className={`widescreen:mr-[calc(16*var(--vw-unit))] fullscreen:widescreen:min-h-[calc(329*var(--vh-unit))] widescreen:min-h-fit widescreen:overflow-y-hidden fullscreen:widescreen:overflow-y-auto scrollbar-custom-gray-99938f scrollbar-w-4 flex flex-col items-start gap-[calc(20*var(--vh-unit))]`}
                        >
                            <div>
                                <div
                                    className={`${parkingData.discountDetail.length > 0 ? 'border-b-[calc(1*var(--vh-unit))] border-[var(--color-text-main01)] pb-[calc(20*var(--vh-unit))]' : ''}`}
                                >
                                    <div
                                        tabIndex={2}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                playTick();
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }
                                        }}
                                        data-interactive
                                        aria-label={t('parking.detail.parkingFeeHistory')}
                                        className="focus-border text-[calc(24*var(--vh-unit))] leading-normal font-bold text-[var(--color-text-main01)]"
                                    >
                                        {t('parking.detail.parkingFeeHistory')}
                                    </div>
                                    <KeyValueRow
                                        tabIndex={2}
                                        label={t('parking.detail.entryTime')}
                                        value={`${formattedData.date} ${formattedData.time}`}
                                        ariaLabel={formattedDateTime}
                                    />
                                    <KeyValueRow tabIndex={2} label={t('parking.detail.parkingTime')} value={`${formattedData.parkingTime}`} />
                                    {parkingData.discountTime > 0 && (
                                        <KeyValueRow tabIndex={2} label={t('parking.detail.discountTime')} value={`${formattedData.discountTime}`} />
                                    )}
                                </div>
                                <div>
                                    {parkingData.discountDetail.length > 0 && (
                                        <div className={`pt-[calc(16*var(--vh-unit))]`}>
                                            <div
                                                tabIndex={2}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        playTick();
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }
                                                }}
                                                data-interactive
                                                aria-label={t('parking.detail.discountDetails')}
                                                className="focus-border text-[calc(24*var(--vh-unit))] leading-normal font-bold text-[var(--color-text-main01)]"
                                            >
                                                {t('parking.detail.discountDetails')}
                                            </div>
                                            {parkingData.discountDetail.map((discount: DiscountDetail, index: number) => (
                                                <KeyValueRow
                                                    key={index}
                                                    tabIndex={2}
                                                    label={discount.name}
                                                    value={`${formatMinutesToKorean(discount.discountTime)}`}
                                                    amount={discount.amount}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div
                            className={`widescreen:mr-[calc(16*var(--vw-unit))] border-t-[calc(1*var(--vh-unit))] border-[var(--color-text-main01)] pb-[calc(20*var(--vh-unit))]`}
                        >
                            <KeyValueRow
                                tabIndex={2}
                                label={t('parking.detail.finalParkingTime')}
                                value={`${formattedData.finalParkingTime}`}
                                labelClassName="text-[var(--color-text-black)] font-bold"
                                valueClassName="text-[var(--color-text-black)] font-bold"
                            />
                            <KeyValueRow
                                tabIndex={2}
                                label={t('parking.detail.estimatedAmount')}
                                value={`${formattedData.amountTotal}원`}
                                labelClassName="text-[var(--color-text-black)] font-bold"
                                valueClassName="text-[var(--color-text-black)] font-bold"
                            />
                        </div>

                        {/* <div className="widescreen:mr-[calc(16*var(--vw-unit))] flex flex-col items-start gap-[calc(7*var(--vh-unit))] self-stretch rounded-[calc(12*var(--vh-unit))] bg-[#EAEAEA] px-[calc(30*var(--vw-unit))] py-[calc(20*var(--vh-unit))]">
                            <div
                                tabIndex={2}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        playTick();
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }
                                }}
                                data-interactive
                                aria-label={t('parking.detail.feeStandard')}
                                className="focus-border text-[calc(22*var(--vh-unit))] leading-normal font-medium text-[var(--color-text-sub01-to-black)]"
                            >
                                {t('parking.detail.feeStandard')}
                            </div>
                            <div className="w-full">
                                {feeStdList.map((fee) => (
                                    <div
                                        key={fee.pafeeInfoSeqno}
                                        tabIndex={2}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                playTick();
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }
                                        }}
                                        data-interactive
                                        aria-label={`${fee.prkTm} ${fee.prkAmt}원`}
                                        className="focus-border flex w-full items-center justify-between gap-[calc(16*var(--vw-unit))]"
                                    >
                                        <div className="text-[calc(22*var(--vh-unit))] leading-[calc(28*var(--vh-unit))] font-medium text-[var(--color-text-sub01-to-black)]">
                                            {fee.prkTm}
                                        </div>
                                        <div className="text-[calc(22*var(--vh-unit))] leading-[calc(28*var(--vh-unit))] font-medium text-[var(--color-text-sub01-to-black)]">
                                            {formatAmount(fee.prkAmt)}원
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div> */}

                        <div
                            tabIndex={2}
                            data-interactive
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    playTick();
                                    e.preventDefault();
                                    e.stopPropagation();
                                }
                            }}
                            aria-label={t('parking.detail.paymentNotice')}
                            className="widescreen:hidden focus-border self-start text-[calc(20*var(--vh-unit))] leading-normal font-medium text-[var(--color-text-sub01)]"
                        >
                            {t('parking.detail.paymentNotice')}
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Additional actions */}
            <ParkingActionButtons
                isUseLocation={isUseLocation ?? false}
                onSearchAgain={handleSearchAgain}
                translationPrefix="parking.detail"
                className="widescreen:hidden"
                handleDirectionGuide={handleDirectionGuide}
            />
        </div>
    );
}
